import { accountId } from './handoffAccount';

// #70 / #72: parcel QR parsing depends only on the account utility, so
// shop start-up in helpers can use it without an import cycle.

// A QR token as the storefront forwards it; anything else is ignored.
const QR_TOKEN_PATTERN = /^[A-Za-z0-9._-]{1,512}$/;

export const parseQrToken = (raw: string | null): string | null =>
  raw !== null && QR_TOKEN_PATTERN.test(raw) ? raw : null;

// Customer Accounts tickets are 32 random bytes in unpadded base64url.
export const parseHandoffToken = (raw: string | null): string | null =>
  raw !== null && raw.length === 43 && /^[A-Za-z0-9_-]{43}$/.test(raw)
    ? raw
    : null;

// Keep a stripped / altered hand-off entry isolated too: never fall back to
// a remembered merchant when the ticket is missing or malformed.
export const isHandoffSearch = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return (
    (params.has('h') || params.has('a')) &&
    !params.get('proof') &&
    !params.get('shop')
  );
};

export const HANDOFF_FLAG = 'standalone_handoff';
export const hasHandoffFlag = (state: unknown): boolean =>
  typeof state === 'object' &&
  state !== null &&
  (state as Record<string, unknown>)[HANDOFF_FLAG] === true;

export const standaloneNavigationState = (
  handoff = false,
  account?: string | null
) => ({
  [STANDALONE_QR_FLAG]: true,
  ...(handoff
    ? { [HANDOFF_FLAG]: true, handoff_account: accountId(account) }
    : {}),
});

// #72: a parcel QR opened full screen carries `rq` and neither the proof nor
// the shop of an embedding storefront (with both it is the STOREFRONT path of
// #70; with only one of them `rq` is ignored as before).
export const isStandaloneQrSearch = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return (
    (parseQrToken(params.get('rq')) !== null || isHandoffSearch(search)) &&
    !params.get('proof') &&
    !params.get('shop')
  );
};

// #72: every navigation of a standalone session marks its history entry with
// this navigation-state flag. A reload keeps the router state but not the
// in-memory session, so the flag alone means: fail closed.
export const STANDALONE_QR_FLAG = 'standalone_qr';

export const hasStandaloneQrFlag = (state: unknown): boolean =>
  typeof state === 'object' &&
  state !== null &&
  (state as Record<string, unknown>)[STANDALONE_QR_FLAG] === true;

// Only the order form redeems `rq`; the path may carry the router base path.
// #154: the old withdrawal lookup (`/withdrawal`) is canonicalized to the
// order form at start-up (src/startup/canonicalize.ts). It stays recognized
// here ONLY as legacy URL recognition for the start-up isolation (no lookup
// screen): if that rewrite fails, the full-screen decision is still taken
// from the initial URL (no remembered shop or token is read) and the router
// fallback redeems the QR on the order form.
export const isStandaloneQrLocation = (
  pathname: string,
  search: string
): boolean =>
  /(^|\/)(order-form-page|withdrawal)\/?$/.test(pathname) &&
  isStandaloneQrSearch(search);
